import React, { useEffect, useMemo, useState } from 'react';
import { Popconfirm, Button, Pagination, Row, Col, Input } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import TableComponent from 'components/table-component';
import { getApi } from '../../redux/sagas/getApiDataSaga';
import { SERVER_IP } from '../../assets/Config';
import { getDateFormat } from '../../services/Utils';
import { deleteApi } from '../../redux/sagas/deleteApiSaga';
import { resetApiStatus } from '../../redux/reducers/globals/globalActions';
import HighlightComponent from '../../components/HighlightComponent';
import AddDrawer from 'pages/master/customer-form';
import { CloseOutlined, EditOutlined } from '@ant-design/icons';
import { AiOutlineSearch } from 'react-icons/ai';

const initialPageSize = 10;
const intialPageSizeOptions = [10, 15, 20];

const Staffs = React.memo(() => {
	const globalRedux = useSelector((state) => state.globalRedux);
	const dispatch = useDispatch();

	const [accBookAddModal, setAccBookAddModal] = useState(false);
	const [searchKey, setSearchKey] = useState('');
	const [tableData, setTableData] = useState(globalRedux.accountBooks);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(initialPageSize);
	const [editAccountBooks, setEditAccountBooks] = useState(null);

	const getAccountBooks = () => {
		let url = `${SERVER_IP}accbook/?orgId=${globalRedux.selectedOrganization._id}`;
		dispatch(getApi('GET_ACCOUNT_BOOKS', url));
	};

	useEffect(() => {
		getAccountBooks();

		if (tableData.length > 0) {
			const tableBtns = document.getElementsByClassName('ant-pagination-item-link');
			const nextBtn = document.getElementsByClassName('ant-pagination-item-link')[tableBtns.length - 1];
			nextBtn.innerHTML = 'Next >';
			nextBtn.style.paddingLeft = '8px';
			nextBtn.style.paddingRight = '8px';
			const prevBtn = document.getElementsByClassName('ant-pagination-item-link')[0];
			prevBtn.innerHTML = '< Previous';
			prevBtn.style.paddingLeft = '8px';
			prevBtn.style.paddingRight = '8px';
		}
	}, []);

	useEffect(() => {
		let doIt = false;
		if (globalRedux.apiStatus.DELETE_ACC_BOOK === 'SUCCESS') {
			dispatch(resetApiStatus('DELETE_ACC_BOOK'));
			doIt = true;
		}
		if (doIt) {
			getAccountBooks();
		}
	}, [globalRedux.apiStatus]);

	useEffect(() => {
		setTableData(globalRedux?.accountBooks);
	}, [globalRedux?.accountBooks]);

	const filteredData = useMemo(() => {
		if (searchKey === '') {
			return tableData;
		}
		return tableData.filter((accBook) => {
			return (
				(accBook?.accbookName || '')?.toLowerCase().includes(searchKey.toLowerCase()) ||
				(accBook?.openingBalance || '')?.toLowerCase().includes(searchKey.toLowerCase()) ||
				(accBook?.closingBalance || '')?.toLowerCase().includes(searchKey.toLowerCase())
			);
		});
	}, [tableData, searchKey]);

	const handleDrawer = (rowData) => {
		setEditAccountBooks(rowData);
		setAccBookAddModal(true);
	};

	const handleStaff = () => {
		setAccBookAddModal(true);
		setEditAccountBooks(null);
	};

	const column = [
		{
			title: 'Account book name',
			dataIndex: 'accbookName',
			key: 'accbookName',
			sorter: (a, b) => a.accbookName.localeCompare(b.accbookName),
			fixed: 'left',
			render: (value) => (
				<HighlightComponent highlightClassName="highlightClass" searchWords={[searchKey]} autoEscape={true} textToHighlight={value} />
			),
		},
		{
			title: 'Opening Balance',
			dataIndex: 'openingBalance',
			key: 'openingBalance',
			sorter: (a, b) => a?.openingBalance - b?.openingBalance,
			align: 'right',
			render: (value) => (
				<HighlightComponent highlightClassName="highlightClass" searchWords={[searchKey]} autoEscape={true} textToHighlight={value} />
			),
		},
		{
			title: 'Closing Balance',
			dataIndex: 'closingBalance',
			key: 'closingBalance',
			sorter: (a, b) => a?.closingBalance - b?.closingBalance,
			align: 'right',
			render: (value) => (
				<HighlightComponent highlightClassName="highlightClass" searchWords={[searchKey]} autoEscape={true} textToHighlight={value} />
			),
		},
		{
			title: 'Created By',
			dataIndex: 'createdBy',
			align: 'left',
			// sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
			render: (value) => value?.firstName,
		},
		{
			title: 'Created Date',
			dataIndex: 'createdAt',
			align: 'left',
			// sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
			render: (value) => getDateFormat(value),
		},

		{
			title: 'Action',
			align: 'center',
			dataIndex: 'accbookName',
			render: (value, row) => {
				return (
					<Row gutter={10} justify="center">
						<Col onClick={() => handleDrawer(row)} className="edit_icon">
							<EditOutlined />
						</Col>
						<Col className="delete_icon">
							<Popconfirm
								title={`Are You Sure to Delete ${value}?`}
								okText="Delete"
								cancelText="No"
								onConfirm={() => {
									let url = `${SERVER_IP}accbook/${row._id}?orgId=${globalRedux.selectedOrganization._id}`;
									dispatch(deleteApi('DELETE_ACC_BOOK', url));
								}}
								placement="rightTop">
								<CloseOutlined />
							</Popconfirm>
						</Col>
					</Row>
				);
			},
		},
	];

	const handleTableChange = (currentPage, pageSize) => {
		setCurrentPage(currentPage);
		setPageSize(pageSize);
	};

	const getStartingValue = () => {
		if (currentPage === 1) return 1;
		else {
			return (currentPage - 1) * pageSize + 1;
		}
	};

	const getEndingValue = () => {
		if (currentPage === 1) return tableData.length < pageSize ? tableData.length : pageSize;
		else {
			let end = currentPage * pageSize;
			return end > tableData.length ? tableData.length : end;
		}
	};

	return (
		<>
			<Row style={{ padding: '20px 10px' }}>
				<Col xl={24}>
					<TableComponent
						className="custom-table"
						style={{ width: '100%' }}
						columns={column}
						bordered
						rowKey={(record) => record._id}
						dataSource={filteredData}
						title={() => (
							<Row justify="space-between">
								<Col md={3}>
									<Input
										placeholder="Search"
										suffix={<AiOutlineSearch />}
										style={{ height: '30px' }}
										onChange={({ target: { value } }) => setSearchKey(value)}
									/>
								</Col>

								<Col md={7}></Col>
								<Col>
									<Button type="primary" style={{ height: '30px', width: '100%' }} onClick={() => handleStaff(true)}>
										Add Account book
									</Button>
								</Col>
							</Row>
						)}
						pagination={{
							current: currentPage,
							pageSize: pageSize,
							position: ['none', 'none'],
						}}
						footer={() => (
							<Row justify="space-between">
								<Col>
									<div>
										{!!filteredData.length &&
											`Showing ${getStartingValue()} - ${getEndingValue()} of ${filteredData.length} Data`}
									</div>
								</Col>
								<Col md={8}>
									<div style={{ textAlign: 'right' }}>
										<Pagination
											pageSizeOptions={intialPageSizeOptions}
											defaultPageSize={initialPageSize}
											showSizeChanger={true}
											total={filteredData.length}
											onChange={handleTableChange}
											responsive
										/>
									</div>
								</Col>
							</Row>
						)}
						// onChange={handleTableChange}
					/>
				</Col>
			</Row>
			<AddDrawer {...{ accBookAddModal, setAccBookAddModal, getAccountBooks, editAccountBooks }} />
		</>
	);
});

export default Staffs;
